/**
 * QaInsights
 * ==========
 *
 * Read-only QA dashboard for the viewer role (also visible to
 * qa_manager + admin for parity). Mirrors QaManagerOverview's shape
 * but strips out every write action: no Delete, no Reassign, no
 * Coached toggle, no Edit. Only View (opens PlaybackPopout in
 * read-mode) + download.
 *
 * Route: `/qa/insights` (RoleGuard module='qa_insights').
 *
 * 2026-07-15 pm — added at Fernando's request after Helen testing
 * feedback: viewer profile needs full read + download of QA
 * evaluations without any manager-level actions.
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { qaApi, recordingsApi } from '../../../api/client';

const STATUSES = ['', 'draft', 'submitted', 'acknowledged', 'disputed'];

function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

export default function QaInsights() {
  const [filterStatus,   setFilterStatus]   = useState('');
  const [filterEvaluator, setFilterEvaluator] = useState('');
  const [filterCoached,  setFilterCoached]  = useState('');

  const evalsQ = useQuery({
    queryKey: ['qa','insights','evals', filterStatus, filterEvaluator, filterCoached],
    queryFn: () => qaApi.listEvaluations({
      status:       filterStatus || undefined,
      evaluator_id: filterEvaluator || undefined,
      coached:      filterCoached === 'yes' ? true : filterCoached === 'no' ? false : undefined,
      limit: 200,
    }),
    staleTime: 15_000,
  });
  const evals = evalsQ.data || [];

  // Team totals (read-only summary at the top).
  const team = useMemo(() => {
    const by = new Map();
    for (const e of evals) {
      const key = e.evaluator_id || '_unassigned';
      if (!by.has(key)) by.set(key, {
        evaluator_id:   e.evaluator_id,
        evaluator_name: e.evaluator_name || '—',
        assigned: 0, evaluated: 0, coached: 0,
      });
      const row = by.get(key);
      if (e.status === 'draft')                                            row.assigned  += 1;
      else if (['submitted','acknowledged','disputed'].includes(e.status)) row.evaluated += 1;
      if (e.coached_at) row.coached += 1;
    }
    return Array.from(by.values()).sort(
      (a, b) => (a.evaluator_name || '').localeCompare(b.evaluator_name || '')
    );
  }, [evals]);

  const openPlayback = (recordingId) => {
    // No ?evaluate=1 flag — popout opens in view mode.
    const url = `${window.location.origin}/playback/${recordingId}`;
    window.open(url, `cx-playback-${recordingId}`, 'width=1400,height=900');
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">QA Insights</h1>
          <div className="text-xs text-surface-muted">
            Read-only QA dashboard. View + download evaluations.
          </div>
        </div>
      </div>

      {/* --- Team summary (read only) --- */}
      <h2 className="text-sm font-semibold mb-2">Team totals</h2>
      <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
            <tr>
              <th className="px-3 py-2 text-left">Evaluator</th>
              <th className="px-3 py-2 text-right">Assigned (draft)</th>
              <th className="px-3 py-2 text-right">Evaluated</th>
              <th className="px-3 py-2 text-right">Coached</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {team.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-surface-muted">
                No evaluations in your tenant yet.
              </td></tr>
            )}
            {team.map((row) => (
              <tr key={row.evaluator_id || '_unassigned'}
                  className="border-t border-surface-border dark:border-surface-dark-border">
                <td className="px-3 py-2">{row.evaluator_name}</td>
                <td className="px-3 py-2 text-right">{row.assigned}</td>
                <td className="px-3 py-2 text-right">{row.evaluated}</td>
                <td className="px-3 py-2 text-right">{row.coached}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    className="text-continuum-600 dark:text-continuum-200 text-sm"
                    onClick={() => setFilterEvaluator(row.evaluator_id || '')}
                  >Filter to their evals →</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* --- Evaluations list (read only + download) --- */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold">Evaluations</h2>
        <button onClick={() => evalsQ.refetch()}
          className="text-xs px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border">
          {evalsQ.isFetching ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-3 text-xs">
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base">
          <option value="">All statuses</option>
          {STATUSES.filter(s => s).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterCoached} onChange={(e) => setFilterCoached(e.target.value)}
          className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base">
          <option value="">Coached: any</option>
          <option value="yes">Coached: yes</option>
          <option value="no">Coached: no</option>
        </select>
        {filterEvaluator && (
          <div className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border">
            evaluator = <code className="text-[10px]">{filterEvaluator.slice(0, 8)}…</code>
            <button className="ml-1" onClick={() => setFilterEvaluator('')} aria-label="Clear evaluator filter">×</button>
          </div>
        )}
        <button
          className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border"
          onClick={() => { setFilterStatus(''); setFilterEvaluator(''); setFilterCoached(''); }}
        >Clear</button>
      </div>

      <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
            <tr>
              <th className="px-3 py-2 text-left">Created</th>
              <th className="px-3 py-2 text-left">Evaluator</th>
              <th className="px-3 py-2 text-left">Form</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Score</th>
              <th className="px-3 py-2 text-left">Coached</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {evals.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-surface-muted">
                No evaluations match these filters.
              </td></tr>
            )}
            {evals.map((ev) => (
              <tr key={ev.id} className="border-t border-surface-border dark:border-surface-dark-border">
                <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDate(ev.created_at)}</td>
                <td className="px-3 py-2 text-xs">{ev.evaluator_name || '—'}</td>
                <td className="px-3 py-2 text-xs">{ev.form_name}</td>
                <td className="px-3 py-2 text-xs">
                  <span className="px-2 py-0.5 rounded-thin bg-surface-subtle dark:bg-surface-dark-subtle">
                    {ev.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-right">
                  {ev.overall_score !== null && ev.overall_score !== undefined
                    ? Number(ev.overall_score).toFixed(1)
                    : '—'}
                </td>
                <td className="px-3 py-2 text-xs">
                  {ev.coached_at ? (
                    <span className="text-blue-600 dark:text-blue-300" title={`by ${ev.coached_by_name || 'unknown'}`}>
                      🎓 Yes
                    </span>
                  ) : (
                    <span className="text-surface-muted">No</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-right space-x-2 whitespace-nowrap">
                  <button
                    className="text-continuum-600 dark:text-continuum-200"
                    onClick={() => openPlayback(ev.recording_id)}
                    title="Open the call playback in a new window (read-only)"
                  >👁 View</button>
                  <a
                    className="text-continuum-600 dark:text-continuum-200"
                    href={recordingsApi.downloadUrl(ev.recording_id)}
                    download
                    title="Download voice recording (WAV)"
                  >↓ Voice</a>
                  <a
                    className="text-continuum-600 dark:text-continuum-200"
                    href={`/api/recordings/${ev.recording_id}/download-combined`}
                    download
                    title="Download combined voice+screen (MP4)"
                  >↓ MP4</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
